// Pure helpers for the Workshop 2 dashboard (mirrors workshop1Data.js style).
// Canonical scorer copied from Activite2.js: prefix matching on the raw labels,
// sum cutoffs <6 -> 1, =6 -> 2, >6 -> 3.

export const NIVEAU_COLORS = {
  1: '#166534',
  2: '#92400e',
  3: '#991b1b'
};

// Built-in threat-category ids — source of truth lives in
// src/components/Atelier 2/Activite1/ThreatCategories.js (BUILT_IN_CATEGORY_IDS).
export const BUILT_IN_W2_CATEGORY_IDS = [
  'etatique',
  'criminel',
  'terroriste',
  'activiste',
  'officine',
  'amateur',
  'vengeur',
  'malveillant'
];

// Fixed-order ebios palette (same order as the ebios ECharts theme).
// Color assignment is keyed to category identity, independent of sort position.
const W2_CATEGORY_PALETTE = [
  '#6366F1',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#06B6D4',
  '#EC4899',
  '#64748B'
];

export const W2_MOTIVATION_ORDER = ['faible', 'moyen', 'eleve'];

export const W2_RESSOURCES_ORDER = ['faibles', 'moyennes', 'importantes'];

const scoreMotivationOrActivite = (raw) => {
  const normalized = String(raw || '').toLowerCase();
  if (normalized.startsWith('elev')) {
    return 3;
  }
  if (normalized.startsWith('moyen')) {
    return 2;
  }
  return 1;
};

const scoreRessources = (raw) => {
  const normalized = String(raw || '').toLowerCase();
  if (normalized.startsWith('import')) {
    return 3;
  }
  if (normalized.startsWith('moyen')) {
    return 2;
  }
  return 1;
};

export const calculateNiveau = (motivation, activite, ressources) => {
  const totalScore = scoreMotivationOrActivite(motivation)
    + scoreMotivationOrActivite(activite)
    + scoreRessources(ressources);
  if (totalScore < 6) {
    return 1;
  }
  if (totalScore === 6) {
    return 2;
  }
  return 3;
};

export const unwrapSource = (source) => {
  if (!source || typeof source !== 'object') {
    return {};
  }
  const nested = source.data && source.data.sources;
  if (Array.isArray(nested) && nested.length > 0 && nested[0] && typeof nested[0] === 'object') {
    return nested[0];
  }
  return source;
};

export const getSourceName = (source) => {
  const record = unwrapSource(source);
  return String(record.name || source.sourceName || source.source?.name || '');
};

export const getSourceIdCandidates = (source) => {
  const record = unwrapSource(source);
  return [
    record.id,
    source.sourceId,
    source.id,
    record.name,
    source.sourceName,
    source.source && source.source.name
  ]
    .filter((value) => value !== undefined && value !== null && String(value) !== '')
    .map((value) => String(value));
};

export const normalizeCategoryId = (raw) => {
  const key = String(raw || 'undefined').trim().toLowerCase();
  return key === '' ? 'undefined' : key;
};

export const isRetainedSource = (source) => unwrapSource(source).selected === true;

export const splitRetainedSources = (sources) => {
  const list = Array.isArray(sources) ? sources : [];
  const retained = [];
  const notRetained = [];
  list.forEach((source) => {
    if (isRetainedSource(source)) {
      retained.push(source);
    } else {
      notRetained.push(source);
    }
  });
  return {
    retained,
    notRetained,
    retainedCount: retained.length,
    totalCount: list.length
  };
};

const prettifyCategoryId = (categoryId) => {
  const clean = String(categoryId || '').replace(/^cat-/, '').replace(/[_-]+/g, ' ').trim();
  if (!clean || clean === 'undefined') {
    return 'Undefined';
  }
  return clean.charAt(0).toUpperCase() + clean.slice(1);
};

export const resolveW2CategoryLabel = (categoryId, options = {}) => {
  const { customCategories = [], t = null } = options;
  const key = normalizeCategoryId(categoryId);
  if (BUILT_IN_W2_CATEGORY_IDS.includes(key)) {
    if (typeof t === 'function') {
      return t(`workshop2.activity1.categories.${key}.name`, prettifyCategoryId(key));
    }
    return prettifyCategoryId(key);
  }
  const custom = (Array.isArray(customCategories) ? customCategories : [])
    .find((entry) => entry && String(entry.id || '').trim().toLowerCase() === key);
  if (custom && custom.name) {
    return String(custom.name);
  }
  if (typeof t === 'function') {
    return t(`workshop2.activity1.categories.${key}.name`, prettifyCategoryId(key));
  }
  return prettifyCategoryId(key);
};

export const colorForW2Category = (categoryId, customOrder = []) => {
  const key = normalizeCategoryId(categoryId);
  const builtInIndex = BUILT_IN_W2_CATEGORY_IDS.indexOf(key);
  if (builtInIndex >= 0) {
    return W2_CATEGORY_PALETTE[builtInIndex % W2_CATEGORY_PALETTE.length];
  }
  const customIndex = (Array.isArray(customOrder) ? customOrder : []).indexOf(key);
  const slot = BUILT_IN_W2_CATEGORY_IDS.length + (customIndex >= 0 ? customIndex : 0);
  return W2_CATEGORY_PALETTE[slot % W2_CATEGORY_PALETTE.length];
};

export const buildCategoryBreakdown = (sources, options = {}) => {
  const { hiddenCategoryIds = [], customCategories = [], t = null } = options;
  const hidden = new Set(
    (Array.isArray(hiddenCategoryIds) ? hiddenCategoryIds : []).map((id) => normalizeCategoryId(id))
  );
  const list = Array.isArray(sources) ? sources : [];
  const byCategory = new Map();
  const customOrder = [];
  list.forEach((source) => {
    const record = unwrapSource(source);
    const key = normalizeCategoryId(record.category || source.category || source.sourceCategory);
    if (hidden.has(key)) {
      return;
    }
    if (!BUILT_IN_W2_CATEGORY_IDS.includes(key) && !customOrder.includes(key)) {
      customOrder.push(key);
    }
    const entry = byCategory.get(key) || { id: key, total: 0, retained: 0, notRetained: 0 };
    entry.total += 1;
    if (record.selected === true) {
      entry.retained += 1;
    } else {
      entry.notRetained += 1;
    }
    byCategory.set(key, entry);
  });
  return [...byCategory.values()]
    .map((entry) => ({
      ...entry,
      label: resolveW2CategoryLabel(entry.id, { customCategories, t }),
      color: colorForW2Category(entry.id, customOrder)
    }))
    .sort((a, b) => b.total - a.total);
};

const motivationIndex = (raw) => {
  const normalized = String(raw || '').toLowerCase();
  if (normalized.startsWith('elev')) {
    return 2;
  }
  if (normalized.startsWith('moyen')) {
    return 1;
  }
  return 0;
};

const ressourcesIndex = (raw) => {
  const normalized = String(raw || '').toLowerCase();
  if (normalized.startsWith('import')) {
    return 2;
  }
  if (normalized.startsWith('moyen')) {
    return 1;
  }
  return 0;
};

export const buildMotivationRessourcesMatrix = (sources) => {
  const list = Array.isArray(sources) ? sources : [];
  const cells = {};
  let total = 0;
  list.forEach((source) => {
    const record = unwrapSource(source);
    const row = motivationIndex(record.motivation);
    const col = ressourcesIndex(record.ressources);
    const niveau = calculateNiveau(record.motivation, record.activite, record.ressources);
    const key = `${col}|${row}`;
    if (!cells[key]) {
      cells[key] = { count: 0, niveauCounts: { 1: 0, 2: 0, 3: 0 }, sources: [] };
    }
    cells[key].count += 1;
    cells[key].niveauCounts[niveau] += 1;
    cells[key].sources.push({
      name: getSourceName(source) || 'Source sans nom',
      activite: String(record.activite || ''),
      niveau
    });
    total += 1;
  });
  const data = [];
  const cellDetails = {};
  W2_MOTIVATION_ORDER.forEach((motivation, row) => {
    W2_RESSOURCES_ORDER.forEach((ressources, col) => {
      const key = `${col}|${row}`;
      const cell = cells[key];
      let dominant = 0;
      if (cell) {
        dominant = [1, 2, 3].reduce((best, level) => {
          if (cell.niveauCounts[level] > cell.niveauCounts[best]) {
            return level;
          }
          if (cell.niveauCounts[level] === cell.niveauCounts[best] && level > best) {
            return level;
          }
          return best;
        }, 1);
        cellDetails[key] = { ...cell, niveau: dominant, motivation, ressources };
      }
      data.push([col, row, dominant]);
    });
  });
  return {
    rowLabels: [...W2_MOTIVATION_ORDER],
    columnLabels: [...W2_RESSOURCES_ORDER],
    data,
    cells: cellDetails,
    total
  };
};

export const buildRetainedNiveauMix = (sources) => {
  const { retained } = splitRetainedSources(sources);
  const counts = { 1: 0, 2: 0, 3: 0 };
  retained.forEach((source) => {
    const record = unwrapSource(source);
    const niveau = calculateNiveau(record.motivation, record.activite, record.ressources);
    counts[niveau] += 1;
  });
  return {
    counts,
    total: retained.length
  };
};

const getMappingSourceCandidates = (mapping) => [
  mapping && mapping.sourceId,
  mapping && mapping.source && mapping.source.id,
  mapping && mapping.sourceName,
  mapping && mapping.source && mapping.source.name
]
  .filter((value) => value !== undefined && value !== null && String(value) !== '')
  .map((value) => String(value));

const getMappingEventCandidates = (mapping) => [
  mapping && mapping.dreadedEventId,
  mapping && mapping.eventId,
  mapping && mapping.dreadedEvent && mapping.dreadedEvent.id,
  mapping && mapping.event && mapping.event.id
]
  .filter((value) => value !== undefined && value !== null && String(value) !== '')
  .map((value) => String(value));

const getEventIdCandidates = (event) => [
  event && event.id,
  event && event._id,
  event && event.name
]
  .filter((value) => value !== undefined && value !== null && String(value) !== '')
  .map((value) => String(value));

export const buildSourceEventMatrix = (sources, mappings, dreadedEvents) => {
  const { retained, totalCount } = splitRetainedSources(sources);
  const events = Array.isArray(dreadedEvents) ? dreadedEvents : [];
  const mappingList = Array.isArray(mappings) ? mappings : [];
  const counts = {};
  mappingList.forEach((mapping) => {
    const sourceKeys = getMappingSourceCandidates(mapping);
    const eventKeys = getMappingEventCandidates(mapping);
    retained.forEach((source, rowIndex) => {
      const sourceKeysForRow = new Set(getSourceIdCandidates(source));
      if (!sourceKeys.some((key) => sourceKeysForRow.has(key))) {
        return;
      }
      events.forEach((event, colIndex) => {
        const eventKeysForCol = new Set(getEventIdCandidates(event));
        if (eventKeys.some((key) => eventKeysForCol.has(key))) {
          const key = `${colIndex}|${rowIndex}`;
          counts[key] = (counts[key] || 0) + 1;
        }
      });
    });
  });
  const keptRows = [];
  retained.forEach((source, rowIndex) => {
    const rowTotal = events.reduce((sum, event, colIndex) => sum + (counts[`${colIndex}|${rowIndex}`] || 0), 0);
    if (rowTotal > 0) {
      keptRows.push({ source, rowIndex, label: getSourceName(source) || `Source ${rowIndex + 1}` });
    }
  });
  const keptCols = [];
  events.forEach((event, colIndex) => {
    const colTotal = retained.reduce((sum, source, rowIndex) => sum + (counts[`${colIndex}|${rowIndex}`] || 0), 0);
    if (colTotal > 0) {
      keptCols.push({ event, colIndex, label: String(event.name || event.id || `Event ${colIndex + 1}`) });
    }
  });
  const data = [];
  let max = 0;
  keptRows.forEach((row, r) => {
    keptCols.forEach((col, c) => {
      const count = counts[`${col.colIndex}|${row.rowIndex}`] || 0;
      if (count > max) {
        max = count;
      }
      data.push([c, r, count]);
    });
  });
  return {
    rowLabels: keptRows.map((row) => row.label),
    columnLabels: keptCols.map((col) => col.label),
    data,
    max,
    retainedCount: retained.length,
    totalCount,
    hasData: data.some((cell) => cell[2] > 0)
  };
};
